/**
 * Zustand slice for rank selection functionality
 *
 * This slice handles rank selection when a character has accumulated enough XP
 * to advance to a new rank. It supports both standard career progression and
 * alternative career ranks.
 *
 * Requirements: 5.1, 5.2, 5.3, 5.4, 6.1, 6.2, 6.3, 8.1, 9.1
 */

import { StateCreator } from 'zustand';
import type {
  Character,
  RankHistoryEntry,
  AlternativeCareerTracking,
  Talent,
  Trait
} from '../../../types/character';
import type { Rank } from '../../../types/advancements';
import type { PrerequisiteStatus } from '../../../types/alternativeRanks';
import { RankService } from '../../../services/RankService';
import { AlternativeRankService } from '../../../services/AlternativeRankService';
import { TalentsService } from '../../../services/TalentsService';
import traitsData from '../../../data/traits.json';

/**
 * A rank option available for selection
 *
 * Represents either a standard career rank or an alternative career rank
 * that the character can select when rank advancement is available.
 */
export interface RankOption {
  /** Type of rank: standard career progression or alternative path */
  type: 'standard' | 'alternative';
  /** The rank number this option represents */
  rank: Rank;
  /** Display name for the rank (e.g., "Seneschal Rank 3" or "Acquisitionist") */
  name: string;
  /** Description of the rank */
  description: string;
  /** Whether this option is available (prerequisites met) */
  available: boolean;
  /** Prerequisite status for alternative ranks */
  prerequisites?: PrerequisiteStatus[];
  /** ID of the alternative rank (for alternative type only) */
  alternativeRankId?: string;
  /** Source rulebook for alternative ranks */
  rulebook?: string;
}

/**
 * Slice interface for rank selection functionality
 */
export interface RankSelectionSlice {
  /**
   * Select the next standard career rank
   * Increments activeCareerRank and adds entry to rankHistory
   * Requirements: 5.1, 5.2, 5.4, 7.3
   */
  selectStandardRank: () => void;

  /**
   * Select an alternative career rank
   * Sets alternativeCareerTracking and adds entry to rankHistory
   * Requirements: 5.1, 5.3, 5.4, 6.1, 6.2, 6.3, 8.1
   */
  selectAlternativeRank: (alternativeRankId: string) => void;

  /**
   * Check if rank selection is pending
   * Returns true if calculated rank > active rank
   * Requirements: 9.1
   */
  isRankSelectionPending: () => boolean;

  /**
   * Get available rank options for selection
   * Includes standard rank and eligible alternative ranks
   * Requirements: 2.1, 3.1, 4.4, 4.5
   */
  getAvailableRankOptions: () => RankOption[];
}

/**
 * Character store interface for the slice
 */
interface CharacterStore extends RankSelectionSlice {
  character: Character | null;
  hasUnsavedChanges: boolean;
}

/**
 * Initialize rank tracking with default values if not present
 */
function ensureRankTracking(character: Character): Character {
  if (!character.rankTracking) {
    const calculatedRank = RankService.calculateRank(character.experience.spent);
    return {
      ...character,
      rankTracking: {
        currentRank: calculatedRank,
        highestRankAchieved: calculatedRank,
        rankHistory: [],
        activeCareerRank: calculatedRank,
        skippedRanks: []
      }
    };
  }
  return character;
}

/**
 * Get the career name from the character's career
 * Capitalizes the career name for display
 */
function getCareerDisplayName(character: Character): string {
  // The career.name is already in display format (e.g., "Seneschal")
  return character.career.name;
}

/**
 * Create the rank selection slice
 */
export const createRankSelectionSlice: StateCreator<CharacterStore, [], [], RankSelectionSlice> = (
  set,
  get
) => ({
  /**
   * Check if rank selection is pending
   *
   * Uses RankService.isRankAdvancementAvailable to compare calculated rank
   * (from spent XP) with the active rank (player's selected rank).
   *
   * Requirements: 9.1
   */
  isRankSelectionPending: (): boolean => {
    const { character } = get();
    if (!character) return false;

    const charWithTracking = ensureRankTracking(character);
    const spentXP = character.experience.spent;
    const activeRank = charWithTracking.rankTracking!.activeCareerRank;

    return RankService.isRankAdvancementAvailable(spentXP, activeRank);
  },

  /**
   * Get available rank options for selection
   *
   * Returns an array of RankOption objects including:
   * - The next standard career rank (if not at max rank)
   * - Eligible alternative ranks for the character's career and rank level
   *
   * Alternative ranks are filtered by:
   * - Career ID matching the character's career
   * - Minimum rank requirement met by active rank
   * - Prerequisites checked for availability status
   *
   * Requirements: 2.1, 3.1, 4.4, 4.5
   */
  getAvailableRankOptions: (): RankOption[] => {
    const { character } = get();
    if (!character) return [];

    const charWithTracking = ensureRankTracking(character);
    const activeRank = charWithTracking.rankTracking!.activeCareerRank;
    const careerId = character.career.id;
    const careerName = getCareerDisplayName(character);
    const alternativeTracking = charWithTracking.rankTracking!.alternativeCareerTracking;

    const options: RankOption[] = [];

    // Get the next standard rank
    const nextStandardRank = RankService.getNextStandardRank(activeRank);

    if (nextStandardRank !== null) {
      // Determine the rank number for display
      // If returning from alternative path, use the return rank
      let displayRank = nextStandardRank;
      let description = `Continue standard career progression to ${careerName} Rank ${nextStandardRank}`;

      if (alternativeTracking?.isOnAlternativePath && alternativeTracking.returnRank) {
        displayRank = alternativeTracking.returnRank;
        description = `Return to standard career path at ${careerName} Rank ${displayRank}`;
      }

      // Standard rank option - always available (no prerequisites)
      options.push({
        type: 'standard',
        rank: displayRank,
        name: `${careerName} Rank ${displayRank}`,
        description,
        available: true
      });
    }

    // Get alternative ranks available at the NEXT rank level (the rank being advanced to)
    // Only show alternative ranks if not currently on an alternative path
    // (Requirement 7.5: prevent re-taking same alternative rank immediately)
    if (!alternativeTracking?.isOnAlternativePath) {
      // Use the next rank (the one being advanced to) for filtering alternative ranks
      // This ensures alternative ranks with minimumRank matching the target rank are shown
      const targetRank = nextStandardRank ?? activeRank;
      const alternativeRanks = AlternativeRankService.getAlternativeRanksAtLevel(
        careerId,
        targetRank
      );

      // Filter out recently completed alternative ranks
      // (Requirement 7.5: same alternative rank excluded after returning)
      const recentlyCompletedAltRankId = alternativeTracking?.currentAlternativeRankId;

      for (const altRank of alternativeRanks) {
        // Skip if this is the same alternative rank we just returned from
        if (recentlyCompletedAltRankId && altRank.id === recentlyCompletedAltRankId) {
          continue;
        }

        // Check prerequisites
        const prerequisiteResult = AlternativeRankService.meetsPrerequisites(
          altRank,
          charWithTracking
        );
        const prerequisiteStatus = AlternativeRankService.getPrerequisiteStatus(
          altRank,
          charWithTracking
        );

        options.push({
          type: 'alternative',
          rank: (activeRank + 1) as Rank, // Alternative ranks advance to next rank level
          name: altRank.name,
          description: altRank.description,
          available: prerequisiteResult.met,
          prerequisites: prerequisiteStatus,
          alternativeRankId: altRank.id,
          rulebook: altRank.rulebook
        });
      }
    }

    return options;
  },

  /**
   * Select the next standard career rank
   *
   * This method:
   * 1. Increments the activeCareerRank by 1
   * 2. Adds an entry to rankHistory
   * 3. Clears alternativeCareerTracking if returning from alternative path
   * 4. Updates highestRankAchieved if necessary
   * 5. Clears recently completed alternative rank ID after taking a standard rank
   *    (allowing the alternative rank to be taken again)
   *
   * Requirements: 5.1, 5.2, 5.4, 7.3, 7.4, 7.5
   */
  selectStandardRank: () => {
    const { character } = get();
    if (!character) return;

    const charWithTracking = ensureRankTracking(character);
    const rankTracking = charWithTracking.rankTracking!;
    const alternativeTracking = rankTracking.alternativeCareerTracking;

    // Determine the new rank
    let newRank: Rank;
    let isReturningFromAlternative = false;

    if (alternativeTracking?.isOnAlternativePath && alternativeTracking.returnRank) {
      // Returning from alternative path - use the return rank
      newRank = alternativeTracking.returnRank;
      isReturningFromAlternative = true;
    } else {
      // Standard progression - increment by 1
      const nextRank = RankService.getNextStandardRank(rankTracking.activeCareerRank);
      if (nextRank === null) {
        // Already at max rank
        return;
      }
      newRank = nextRank;
    }

    // Create history entry
    const historyEntry: RankHistoryEntry = {
      rank: newRank,
      achievedAt: new Date(),
      spentXPAtAchievement: character.experience.spent,
      isAlternativeRank: false
    };

    // Update highest rank achieved if necessary
    const newHighestRank = Math.max(rankTracking.highestRankAchieved, newRank) as Rank;

    // Handle alternative tracking based on current state
    // Requirement 7.3: Clear alternativeCareerTracking when returning from alternative path
    // Requirement 7.5: Prevent re-taking same alternative rank until standard rank taken
    let newAlternativeTracking: AlternativeCareerTracking | undefined;

    if (isReturningFromAlternative) {
      // Returning from alternative path - preserve the completed alternative rank ID
      // to prevent immediate re-selection (Requirement 7.5)
      newAlternativeTracking = {
        isOnAlternativePath: false,
        currentAlternativeRankId: alternativeTracking!.currentAlternativeRankId,
        currentAlternativeRankName: undefined,
        skippedStandardRank: undefined,
        returnRank: undefined
      };
    } else if (
      alternativeTracking?.currentAlternativeRankId &&
      !alternativeTracking.isOnAlternativePath
    ) {
      // Taking a standard rank after having returned from alternative path
      // Clear the recently completed alternative rank ID so it can be taken again
      // (Requirement 7.5: alternative rank becomes available after taking standard rank)
      newAlternativeTracking = undefined;
    }
    // If no alternative tracking exists, leave it undefined

    set({
      character: {
        ...charWithTracking,
        rankTracking: {
          ...rankTracking,
          activeCareerRank: newRank,
          currentRank: newRank,
          highestRankAchieved: newHighestRank,
          rankHistory: [...rankTracking.rankHistory, historyEntry],
          alternativeCareerTracking: newAlternativeTracking
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  /**
   * Select an alternative career rank
   *
   * This method:
   * 1. Sets activeCareerRank to current rank + 1
   * 2. Sets alternativeCareerTracking with all required fields
   * 3. Adds the skipped standard rank to skippedRanks array
   * 4. Adds an entry to rankHistory with alternative rank info
   *
   * Requirements: 5.1, 5.3, 5.4, 6.1, 6.2, 6.3, 8.1
   */
  selectAlternativeRank: (alternativeRankId: string) => {
    const { character } = get();
    if (!character) return;

    // Get the alternative rank details
    const alternativeRank = AlternativeRankService.getAlternativeRankById(alternativeRankId);
    if (!alternativeRank) {
      console.error(`Alternative rank not found: ${alternativeRankId}`);
      return;
    }

    const charWithTracking = ensureRankTracking(character);
    const rankTracking = charWithTracking.rankTracking!;

    // Verify auto-verifiable prerequisites are met (skill, talent, characteristic)
    // "Other" type prerequisites are verified manually in the UI before this is called
    const prerequisiteResult = AlternativeRankService.meetsPrerequisites(
      alternativeRank,
      charWithTracking
    );

    // Check if all unmet prerequisites are of type "other" (manually verified in UI)
    const hasUnmetAutoPrereqs = prerequisiteResult.unmetPrerequisites.some(
      (prereq) => prereq.type !== 'other'
    );

    if (hasUnmetAutoPrereqs) {
      console.error(
        `Auto-verifiable prerequisites not met for alternative rank: ${alternativeRankId}`,
        prerequisiteResult.unmetPrerequisites.filter((p) => p.type !== 'other')
      );
      return;
    }

    // Calculate the new rank (current + 1)
    const newRank = (rankTracking.activeCareerRank + 1) as Rank;

    // The skipped standard rank is the rank we would have taken
    const skippedStandardRank = newRank;

    // The return rank is the rank after the skipped one
    const returnRank = (skippedStandardRank + 1) as Rank;

    // Create alternative career tracking
    const newAlternativeTracking: AlternativeCareerTracking = {
      isOnAlternativePath: true,
      currentAlternativeRankId: alternativeRankId,
      currentAlternativeRankName: alternativeRank.name,
      skippedStandardRank,
      returnRank: returnRank <= 8 ? returnRank : undefined
    };

    // Add skipped rank to skippedRanks array
    const existingSkippedRanks = rankTracking.skippedRanks || [];
    const newSkippedRanks = existingSkippedRanks.includes(skippedStandardRank)
      ? existingSkippedRanks
      : [...existingSkippedRanks, skippedStandardRank];

    // Create history entry
    const historyEntry: RankHistoryEntry = {
      rank: newRank,
      achievedAt: new Date(),
      spentXPAtAchievement: character.experience.spent,
      isAlternativeRank: true,
      alternativeRankId,
      alternativeRankName: alternativeRank.name
    };

    // Update highest rank achieved if necessary
    const newHighestRank = Math.max(rankTracking.highestRankAchieved, newRank) as Rank;

    // Grant talents and traits from alternative rank definition (REQ-4 AC-4.3, 4.5, 4.6)
    const grantedTalents: Talent[] = [];
    for (const talentId of alternativeRank.grantedTalents ?? []) {
      if (charWithTracking.talents.some((t) => t.id === talentId)) continue;
      const def = TalentsService.getTalentById(talentId);
      if (!def) {
        console.warn(`grantedTalents: talent not found: ${talentId}`);
        continue;
      }
      grantedTalents.push({
        id: def.id,
        name: def.name,
        tier: def.tier,
        prerequisites: def.prerequisites ?? [],
        shortDescription: def.shortDescription ?? '',
        fullDescription: def.fullDescription ?? '',
        rulebook: def.rulebook ?? 'core',
        baseTalent: def.baseTalent,
        specialization: def.specialization,
        purchaseCount: 1,
        purchaseHistory: [
          {
            advancementId: `auto-grant:${alternativeRankId}:${def.id}`,
            purchasedAt: new Date(),
            rank: newRank
          }
        ],
        source: 'AlternativeRank'
      });
    }

    const grantedTraits: Trait[] = [];
    for (const traitId of alternativeRank.grantedTraits ?? []) {
      if (charWithTracking.traits.some((t) => t.id === traitId)) continue;
      const def = (traitsData.traits as Trait[]).find((t) => t.id === traitId);
      if (!def) {
        console.warn(`grantedTraits: trait not found: ${traitId}`);
        continue;
      }
      grantedTraits.push(def);
    }

    set({
      character: {
        ...charWithTracking,
        talents: grantedTalents.length
          ? [...charWithTracking.talents, ...grantedTalents]
          : charWithTracking.talents,
        traits: grantedTraits.length
          ? [...charWithTracking.traits, ...grantedTraits]
          : charWithTracking.traits,
        rankTracking: {
          ...rankTracking,
          activeCareerRank: newRank,
          currentRank: newRank,
          highestRankAchieved: newHighestRank,
          rankHistory: [...rankTracking.rankHistory, historyEntry],
          alternativeCareerTracking: newAlternativeTracking,
          skippedRanks: newSkippedRanks
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  }
});
